import {
  ArrowLeftOutlined,
  FilterFilled,
  FilterOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Modal,
  Radio,
  Tag,
  Tooltip,
} from 'antd';
import { debounce, filter, forEach, isEmpty, map } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import MappedUnitsCell from '../../../../../components/MappedUnitsCell';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/svg/info-solid.svg';
import { ReactComponent as FilterIcon } from '../../../../../assets/svg/sliders-solid.svg';
import AllFilterOrderModal from '../../../../../libraryModules/orders/modules/all/components/AllFilterOrderModal';
import AllOrderItemModal from '../../../../../libraryModules/orders/modules/all/components/AllOrderItemModal';
import AllOrderList from '../../../../../libraryModules/orders/modules/all/components/AllOrderList';

import ORDER_TAG_BULK_ACTIONS, {
  CANCELLATION_TYPE,
  COMMENT_ORDER_PHASE,
  COMMENT_TARGET_TYPE,
  DATE_FORMATS,
  DELIVERY_TYPE,
  DELIVERY_TYPE_OPTIONS,
  DISABLE_ORDER,
  DISABLE_ORDER_ITEM,
  FULFILLMENT_STATUS,
  LIMIT,
  NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS,
  NEW_ORDER_TABLE_ACTIONS_OPTIONS,
  ORDER_STATUS,
  ORDER_TAG_TYPES,
  ORDER_TYPE,
  PERMISSION_TYPE,
  PERMISSIONS_KEY,
  ROUTES,
  SORT,
  SORTING,
  STOCK_COLUMN_TOOLTIPS,
  TRANSFER_ORDER_STATUS,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  formatDate,
  hasNonEmptyValueObj,
  hasPermission,
  hasPreOrderToy,
  humanizeEnum,
} from '../../../../../common/utils';
import BulkActionButton from '../../../../../components/BulkActionButton';
import ChallanDownloadButton from '../../../../../components/ChallanDownloadButton';
import ColumnTitleWithInfo from '../../../../../components/ColumnTitleWithInfo';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonPreviewTable from '../../../../../components/CommonPreviewTable';
import CommonTable from '../../../../../components/CommonTable';
import FulfilmentPossibilityDrawer from '../../../../../components/FulfilmentPossibilityDrawer';
import LoaderComponent from '../../../../../components/LoaderComponent';
import OrderDynamicTags from '../../../../../components/OrderDynamicTags';
import RenderCopyIcon from '../../../../../components/RenderCopyIcon';
import SearchComponent from '../../../../../components/SearchComponent';
import SelectComponent from '../../../../../components/SelectComponent';
import SortDropdown from '../../../../../components/SortDropdown';
import { PINCODES } from '../../../../../components/graphql/Queries';
import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import AddCommentModal from '../../../../../libraryModules/orders/modules/all/components/AddCommentModal';
import AddDelayReasonModal from '../../../../../libraryModules/orders/modules/all/components/AddDelayReasonModal';
import { ORDERS_SELECT } from '../../../../../libraryModules/orders/modules/all/graphql/Queries';
import { USERS } from '../../../../../libraryModules/raiseRequest/graphql/Queries';
import UserSelect from '../../../../payments/modules/endUser/components/UserSelect';
import ResolveCommentModal from '../../../../requests/modules/library/components/ResolveCommentModal';
import ContentMasterModal from '../../all/components/ContentMasterModal';
import DelayReasonModal from '../../all/components/DelayReasonModal';
import ItemStatusModal from '../../all/components/ItemStatusModal';
import NoteModal from '../../all/components/NoteModal';
import {
  CANCEL_ORDER_ADMIN,
  CREATE_COMMENT,
  RESET_ORDER_CHALLAN_ADMIN,
  RESET_ORDER_ITEM_STICKER_ADMIN,
} from '../../all/graphql/Mutations';
import {
  GET_ORDER_CHALLAN_DOWNLOAD_URL_ADMIN,
  GET_ORDER_STICKER_DOWNLOAD_URL_ADMIN,
  ORDER_ITEMS_ADMIN,
} from '../../all/graphql/Queries';
import AssignDeliveryPartnerModal from '../../new/components/AssignDeliveryPartnerModal';
import BigToyIcon from '../../new/components/BigToyIcon';
import StatusFlag from '../../new/components/OrderTagRenderer';
import {
  ASSIGN_DELIVERY_PARTNER_V2,
  UPDATE_ORDER_METADATA,
  UPDATE_ORDER_STATUS_ADMIN,
  UPDATE_ORDER_STATUS_ADMIN_V2,
} from '../../new/graphql/Mutations';
import { USER_ORDERS_ADMIN } from '../../new/graphql/Queries';
import useToyDrawer from '../../../../../hooks/useToyDrawer';
import createOrderItemStaffColumns from '../../../../../components/OrderItemStaff/createOrderItemStaffColumns';
import { isUnitsFeatureEnabledForLibrary } from '../../../../../hooks/useInventoryUnitsFeature';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialNewDeliveryFilter = {
  limit: LIMIT,
  skip: 0,
};

const initialNewDeliverySort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

let filterScrollDebounceJob = null;

const sort = [
  {
    label: SORTING?.ORDER_ID_A_Z?.label,
    value: SORTING?.ORDER_ID_A_Z?.value,
    field: SORTING?.ORDER_ID_A_Z?.field,
    sort: SORTING?.ORDER_ID_A_Z?.sort,
  },
  {
    label: SORTING?.ORDER_ID_Z_A?.label,
    value: SORTING?.ORDER_ID_Z_A?.value,
    field: SORTING?.ORDER_ID_Z_A?.field,
    sort: SORTING?.ORDER_ID_Z_A?.sort,
  },
];

const NewOrderTable = ({
  isAssignDelPartnerTab,
  userId: user,
  isSystemUsersTab,
}) => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [delayForm] = Form.useForm();
  const [assignDelPartnerForm] = Form.useForm();
  const {
    state: {
      permissions,
      isDesktop,
      commonPermissions: {
        isUserPermission,
        isToyPermission,
        isDeliveryPartnerPermission,
        isLibraryPermission,
      },
    },
  } = useContext(AppContext);
  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.ORDERS_ALL_DELIVERY,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  const history = useHistory();

  const { openToyDrawer } = useToyDrawer();
  const queryParams = new URLSearchParams(location?.search);
  const orderId = queryParams?.get('orderId') || '';
  const [orderList, setOrderList] = useState([]);
  const [possibilityDrawerVisible, setPossibilityDrawerVisible] = useState(
    false,
  );
  const [selectedPossibilityItem, setSelectedPossibilityItem] = useState(null);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [isEmptyOrderList, setIsEmptyOrderList] = useState(false);
  const [order, setOrder] = useState({});
  const [orderItem, setOrderItem] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isActive, setIsActive] = useState(true);

  const [isOrderLoading, setIsOrderLoading] = useState(true);
  const [isNotePrompts, setIsNotePrompts] = useState(false);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isAddCommentLoading, setIsAddCommentLoading] = useState(false);
  const [isOrderAction, setIsOrderAction] = useState(false);
  const [isFulfillLoading, setIsFulfillLoading] = useState(false);
  const [isFulfillPrompts, setIsFulfillPrompts] = useState(false);
  const [isReadyLoading, setIsReadyLoading] = useState(false);
  const [isReadyPrompts, setIsReadyPrompts] = useState(false);
  const [isDelayReasonPrompts, setIsDelayReasonPrompts] = useState(false);
  const [isConfirmPrompts, setIsConfirmPrompts] = useState(false);
  const [isReasonPrompt, setIsReasonPrompt] = useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [nestedData, setNestedData] = useState({});
  const getStaffColumns = createOrderItemStaffColumns({ canEdit: false });
  const [isLoading, setIsLoading] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [toyPreviewImage, setToyPreviewImage] = useState();
  const [isContentMasterModalOpen, setIsContentMasterModalOpen] = useState(
    false,
  );
  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isAllOrderItemModalOpen, setIsAllOrderItemModalOpen] = useState(false);
  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);

  const [isCommentLoading, setIsCommentLoading] = useState(false);

  const [isCommentPrompts, setIsCommentPrompts] = useState(false);

  const [isChallanLoading, setIsChallanLoading] = useState({});

  const [isResetLoading, setIsResetLoading] = useState({});

  const [isOrderStatusModalOpen, setIsOrderStatusModalOpen] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isDeliveryPartnerLoading, setIsDeliveryPartnerLoading] = useState(
    false,
  );
  const [isDeliveryPartnerPrompts, setIsDeliveryPartnerPrompts] = useState(
    false,
  );
  const [deliveryPartnerData, setDeliveryPartnerData] = useState({});
  const [isDeliveryPartnerModalOpen, setDeliveryPartnerModalOpen] = useState(
    false,
  );
  const userParams = useParams();
  const userId = isSystemUsersTab
    ? user || userParams?.id
    : user || userParams?.userId;

  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const tempComment = useRef(null);
  const tempUnfulfilledReason = useRef(null);

  const [getOrderChallanDownloadUrlAdmin] = useLazyQuery(
    GET_ORDER_CHALLAN_DOWNLOAD_URL_ADMIN,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const handleChallanDownload = async (
    id,
    windowReference,
    orderIds = null,
  ) => {
    setIsChallanLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    const response = await getOrderChallanDownloadUrlAdmin({
      variables: {
        where: {
          id,
        },
        ...(orderIds ? { orderIds } : {}),
      },
    });
    if (response?.data) {
      // eslint-disable-next-line no-param-reassign
      windowReference.location =
        response?.data?.getOrderChallanDownloadUrlAdmin?.challanUrl;
    }
    setIsChallanLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };
  const [createComment] = useMutation(CREATE_COMMENT, {
    onError: () => {},
  });

  const statusFilter = isAssignDelPartnerTab
    ? [ORDER_STATUS?.READY_TO_SHIP?.value, ORDER_STATUS?.CONFIRMED?.value]
    : [];
  const [userOrdersWithItems] = useLazyQuery(USER_ORDERS_ADMIN, {
    onCompleted: (response) => {
      setOrderList([...response?.userOrdersWithItems?.data]);
      if (
        response?.userOrdersWithItems?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyOrderList(true);
      } else {
        setIsEmptyOrderList(false);
      }

      if (response?.userOrdersWithItems?.data?.length < LIMIT) {
        setIsActive(false);
      }
      if (
        response?.userOrdersWithItems?.data?.length < LIMIT ||
        orderList.length + response?.userOrdersWithItems?.data?.length >=
          response?.orders?.count
      ) {
        setHasMore(false);
      }

      setIsFetchMoreOrderLoading(false);

      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.userOrdersWithItems?.count,
      };
      setPaginationProp(pagination);
      setIsOrderLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const handleAddComment = async (values) => {
    setIsCommentLoading(true);
    const response = await createComment({
      variables: {
        data: {
          targetType: isOrderAction
            ? COMMENT_TARGET_TYPE?.ORDER
            : COMMENT_TARGET_TYPE?.ORDER_ITEM,
          targetId: isOrderAction ? order?.id : orderItem?.id,
          metaData: {
            orderPhase: isOrderAction
              ? COMMENT_ORDER_PHASE?.ORDER
              : COMMENT_ORDER_PHASE?.UNFULFILLED,
          },
          isResolved: true,
          comment: values?.comment?.trim(),
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsCommentPrompts(false);
      setNestedData({});
      setExpandedRowKeys([]);
      form?.resetFields();
      userOrdersWithItems({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsCommentLoading(false);
  };

  const [orderItemsAdmin] = useLazyQuery(ORDER_ITEMS_ADMIN, {
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const [users, { loading: isUsersLoading }] = useLazyQuery(USERS, {
    onCompleted: (response) => {
      if (response?.users?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.users?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.users?.data, (item) =>
        optionsCopy?.push({
          label: `${
            item?.firstName || item?.lastName
              ? `${item?.firstName || ''} ${item?.lastName || ''}`
              : '-'
          }`,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      if (response?.pincodes?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.pincodes?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.pincodes?.data, (item) =>
        optionsCopy?.push({
          label: item?.pincode,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });
  const [orderSelect, { loading: isOrderSelectLoading }] = useLazyQuery(
    ORDERS_SELECT,
    {
      onCompleted: (response) => {
        if (response?.userOrdersWithItems?.data?.length < LIMIT) {
          setFilterIsEnd(true);
        }
        if (response?.userOrdersWithItems?.count === 0) {
          setIsEmptyFilterList(true);
        } else {
          setIsEmptyFilterList(false);
        }
        const optionsCopy = [];
        forEach(response?.userOrdersWithItems?.data, (item) =>
          optionsCopy?.push({
            label: item?.orderNumber,
            key: item?.id,
          }),
        );
        setFilterList([...filterList, ...optionsCopy]);
        setFilterLoading(false);
      },
      fetchPolicy: 'network-only',
      onError() {
        setFilterLoading(false);
      },
    },
  );

  const [cancelOrderAdmin] = useMutation(CANCEL_ORDER_ADMIN, {
    onError: () => {},
  });

  const [updateOrderStatusAdmin] = useMutation(UPDATE_ORDER_STATUS_ADMIN, {
    onError: () => {},
  });

  const [updateOrderMetadata] = useMutation(UPDATE_ORDER_METADATA, {
    onError: () => {},
  });

  const [updateOrderStatusAdminV2] = useMutation(UPDATE_ORDER_STATUS_ADMIN_V2, {
    onError: () => {},
  });

  const [assignDeliveryPartnerV2] = useMutation(ASSIGN_DELIVERY_PARTNER_V2, {
    onError: () => {},
  });
  const [resetOrderChallanAdmin] = useMutation(RESET_ORDER_CHALLAN_ADMIN, {
    onError: () => {},
  });
  const handleChallanReset = async (id) => {
    setIsResetLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    await resetOrderChallanAdmin({
      variables: {
        where: {
          id,
        },
      },
    });
    setIsResetLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  useEffect(() => {
    setOrderSearchTerm(orderId || orderSearchTerm);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsOrderLoading(true);
    setOrderList([]);
    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  }, []);

  const [isResetStickerLoading, setIsResetStickerLoading] = useState({});

  const [resetOrderItemStickerAdmin] = useMutation(
    RESET_ORDER_ITEM_STICKER_ADMIN,
    {
      onError: () => {},
    },
  );

  const handleStickerReset = async (id) => {
    setIsResetStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    await resetOrderItemStickerAdmin({
      variables: {
        where: {
          id,
        },
      },
    });
    setIsResetStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = isEmpty(pagination)
      ? 0
      : (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsOrderLoading(true);
    setSortedInfo(sorter);
    setNestedData({});
    setExpandedRowKeys([]);
    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          skip: skip ?? 0,
          limit: pagination?.pageSize || LIMIT,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const isLastItem = (orderIds) => {
    const orderData = nestedData?.[orderIds]?.orderItemsAdmin?.data || [];
    const processedCount = orderData?.filter(
      (item) =>
        item?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.CANCELLED?.value,
    )?.length;

    const totalItems = orderData?.length;

    return processedCount + 1 >= totalItems;
  };

  const updateMainOrderList = (orderIds, commentData) => {
    setOrderList((prev) =>
      prev.map((orders) => {
        if (orders?.id === orderIds) {
          return {
            ...orders,
            metadata: {
              ...commentData,
            },
          };
        }
        return orders;
      }),
    );
  };

  const updateNestedDataLocally = (orderIds, orderItemId, newStatus) => {
    setNestedData((prev) => ({
      ...prev,
      [orderIds]: {
        ...prev?.[orderIds],
        orderItemsAdmin: {
          ...prev?.[orderIds]?.orderItemsAdmin,
          data:
            prev?.[orderIds]?.orderItemsAdmin?.data?.map((item) =>
              item?.id === orderItemId
                ? {
                    ...item,
                    status: newStatus,

                    cancellationData:
                      newStatus === FULFILLMENT_STATUS.UNFULFILLED?.value
                        ? { ...tempUnfulfilledReason.current }
                        : null,
                  }
                : item,
            ) || [],
        },
      },
    }));
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setOrderSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsOrderLoading(true);
    setOrderList([]);
    setNestedData({});
    setExpandedRowKeys([]);
    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const handleAddCommentInOrder = async (values) => {
    setIsAddCommentLoading(true);
    const response = await cancelOrderAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsAddCommentModalOpen(false);
      setNestedData({});
      setExpandedRowKeys([]);
      setOrderList([]);
      form?.resetFields();
      userOrdersWithItems({
        variables: {
          where: {
            id: userId,
          },
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsAddCommentLoading(false);
  };

  const handleAddCommentInOrderItem = async (values) => {
    setIsAddCommentLoading(true);
    const response = await updateOrderStatusAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
          status: FULFILLMENT_STATUS?.UNFULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      setIsAddCommentModalOpen(false);
      if (isLastItem(order?.id, orderItem?.id)) {
        setIsOrderLoading(true);
        setNestedData({});
        setExpandedRowKeys([]);
        setOrderList([]);
        form?.resetFields();
        userOrdersWithItems({
          variables: {
            where: {
              id: userId,
            },
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        tempUnfulfilledReason.current = {
          reason: values?.reason,
          additionalComment: values?.comment?.trim(),
          type: CANCELLATION_TYPE?.LIBRARY?.value,
          date: Date(),
        };

        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.UNFULFILLED?.value,
        );
      }
    }
    form?.resetFields();
    setIsAddCommentLoading(false);
  };

  const handleFulfillOrderItem = async () => {
    setIsFulfillLoading(true);
    const response = await updateOrderStatusAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          status: FULFILLMENT_STATUS?.FULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      if (isLastItem(order?.id, orderItem?.id)) {
        setIsOrderLoading(true);
        setIsFulfillPrompts(false);
        setNestedData({});
        setOrderList([]);
        setExpandedRowKeys([]);
        userOrdersWithItems({
          variables: {
            where: {
              id: userId,
            },
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.FULFILLED?.value,
        );

        if (tempComment?.current?.[order?.id])
          updateMainOrderList(order?.id, tempComment?.current?.[order?.id]);
      }
      setIsDelayReasonPrompts(false);
      delayForm?.resetFields();
      setIsReadyLoading(false);
    }
    setIsFulfillLoading(false);
  };

  const handleReadyOrder = async (values) => {
    setIsReadyLoading(true);
    const response = await updateOrderStatusAdminV2({
      variables: {
        where: {
          id: values,
        },
        data: {
          status: ORDER_STATUS?.CONFIRMED?.value,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsReadyPrompts(false);
      setNestedData({});
      setOrderList([]);
      setSelectedRowKeys([]);
      setExpandedRowKeys([]);
      setIsConfirmPrompts(false);
      userOrdersWithItems({
        variables: {
          where: {
            id: userId,
          },
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsReadyLoading(false);
    setIsDelayReasonPrompts(false);
    delayForm?.resetFields();
  };
  const handleAddDelayReason = async (values) => {
    setIsReadyLoading(true);
    const metadataResponse = await updateOrderMetadata({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          metadata: [
            { key: 'delayReason', value: values?.reason },
            { key: 'delayNote', value: values?.comment },
          ],
        },
      },
    });
    if (metadataResponse?.data) {
      tempComment.current = {
        [order?.id]: {
          delayReason: { value: values?.reason },
          delayNote: { value: values?.comment },
        },
      };
      if (isOrderAction) {
        handleReadyOrder([order?.id]);
      } else {
        handleFulfillOrderItem();
      }
    } else {
      setIsReadyLoading(false);
    }
  };

  const handleAssignDeliveryPartner = async (rowKeys, deliveryPartner) => {
    setIsDeliveryPartnerLoading(true);
    const response = await assignDeliveryPartnerV2({
      variables: {
        where: {
          id: rowKeys,
        },
        data: {
          deliveryPartnerId: deliveryPartner,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setDeliveryPartnerModalOpen(false);
      setNestedData({});
      setOrderList([]);
      setExpandedRowKeys([]);
      setSelectedRowKeys([]);
      setIsDeliveryPartnerPrompts(false);
      setDeliveryPartnerData({});
      assignDelPartnerForm.resetFields();
      userOrdersWithItems({
        variables: {
          where: {
            id: userId,
          },
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsDeliveryPartnerLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          status:
            whereFilter?.status?.length > 0
              ? whereFilter?.status
              : statusFilter,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    let whereFiltersCopy;
    if (dataIndex === 'deliveryType') {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: null,
      };
    } else {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: [],
      };
    }
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : statusFilter,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          limit: paginationProp?.pageSize || LIMIT,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];
      const orderStatus = map(
        filter(
          ORDER_STATUS,
          (item) =>
            (isAssignDelPartnerTab
              ? item?.value === ORDER_STATUS?.READY_TO_SHIP?.value
              : item?.value === ORDER_STATUS?.PLACED?.value) ||
            item?.value === ORDER_STATUS?.CONFIRMED?.value,
        ),
        (item) => item,
      );
      switch (filterIndex) {
        case 'deliveryPartnerName':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.DELIVERY?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'users':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.USER?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'status':
          forEach(ORDER_STATUS, (item) => {
            optionsCopy?.push({ label: item?.text, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'pincodes':
          pincodes({
            variables: {
              filter: {
                ...initialTableFilter,
              },
              sort: {
                field: 'pincode',
                order: 'ASC',
              },
            },
          });
          break;

        case 'deliveryType':
          forEach(DELIVERY_TYPE_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'orderNumber':
          orderSelect({
            variables: {
              filter: {
                ...initialTableFilter,
                orderType: ORDER_TYPE?.NEW,
              },
              sort: initialTableSort,
            },
          });
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (dataIndex === 'status') {
      return;
    }
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (
      !isUsersLoading &&
      !isPincodesLoading &&
      !isOrderSelectLoading &&
      !filterIsEnd
    ) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'deliveryPartnerName':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.DELIVERY?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'users':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.USER?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'pincodes':
              pincodes({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: {
                    field: 'pincode',
                    order: 'ASC',
                  },
                },
              });
              break;

            case 'orderNumber':
              orderSelect({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    orderType: ORDER_TYPE?.NEW,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'deliveryPartnerName':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.DELIVERY?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'users':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.USER?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'pincodes':
        pincodes({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: {
              field: 'pincode',
              order: 'ASC',
            },
          },
        });
        break;

      case 'orderNumber':
        orderSelect({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              orderType: ORDER_TYPE?.NEW,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt' || dataIndex === 'deliveredOn') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'deliveryType') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={DELIVERY_TYPE_OPTIONS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(isVisible);
    },
  });

  const [isStickerLoading, setIsStickerLoading] = useState({});

  const [getOrderStickerDownloadUrlAdmin] = useLazyQuery(
    GET_ORDER_STICKER_DOWNLOAD_URL_ADMIN,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const handleStickerDownload = async (id, windowReference) => {
    setIsStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    const response = await getOrderStickerDownloadUrlAdmin({
      variables: {
        where: {
          id,
        },
      },
    });
    if (response?.data) {
      // eslint-disable-next-line no-param-reassign
      windowReference.location =
        response?.data?.getOrderStickerDownloadUrlAdmin?.stickerUrl;
    }
    setIsStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  const handleExpand = async (expanded, record) => {
    setIsLoading({
      [record?.id]: true,
    });
    setIsDisabled(true);
    if (!nestedData?.[record?.id]) {
      const res = await orderItemsAdmin({
        variables: {
          where: {
            id: record?.id,
          },
        },
      });
      if (res?.data) {
        setNestedData((state) => ({
          ...state,
          [record?.id]: res?.data,
        }));
      }
      setIsDisabled(false);
    }
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record?.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys?.filter((key) => key !== record?.id));
    }
    setIsLoading({
      [record?.id]: false,
    });
    setIsDisabled(false);
  };

  const handleOrderChange = (value, record) => {
    setOrder(record);
    setIsOrderAction(true);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsReadyPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const handleOrderItemChange = (value, record, orderDetails) => {
    setOrderItem(record);
    setOrder(orderDetails);
    setIsOrderAction(false);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsFulfillPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const hasSelected = selectedRowKeys.length > 0;

  const renderBlinkingDotEl = (orderStatus) => {
    if (
      orderStatus === ORDER_STATUS.CONFIRMED.value ||
      orderStatus === ORDER_STATUS.PLACED.value
    ) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    return null;
  };

  const columns = [
    {
      title: 'ORDER ID',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      ellipsis: true,
      fixed: 'left',
      className: 'max-width-column',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (_, record) => (
        <div className="d-flex align-center">
          <RenderCopyIcon copyText={record?.orderNumber} />
          {renderBlinkingDotEl(record?.status)}
          {record?.orderNumber}

          {record?.isFirstOrder && (
            <StatusFlag type={ORDER_TAG_TYPES.FIRST_ORDER} />
          )}
          {record?.delayedInConfirmed && (
            <StatusFlag type={ORDER_TAG_TYPES.DELAYED_CONFIRMED} />
          )}
          {record?.metadata?.isContainBigToy && <BigToyIcon />}
          {hasPreOrderToy(record) && (
            <StatusFlag type={ORDER_TAG_TYPES.PRE_ORDER} />
          )}
          <OrderDynamicTags tags={record?.orderTags} />
        </div>
      ),
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      align: 'left',
      width: 100,
      className: 'max-width-column',
      render: (_, record) => record?.orderItemCount || '-',
    },
    {
      title: 'DELIVERY TYPE',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) =>
        DELIVERY_TYPE?.[record?.deliveryType]?.label || '-',
    },
    {
      title: 'TOTAL ORDERS COUNT',
      dataIndex: 'totalOrdersCount',
      key: 'totalOrdersCount',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => record?.totalOrdersCount ?? '-',
    },
    {
      title: 'DELAY REASON',
      dataIndex: 'delayReason',
      key: 'delayReason',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {!isEmpty(record?.metadata?.delayReason) ? (
            <Tooltip title="View reason" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsReasonPrompt(true);
                  setOrder(record);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'DELIVERY PARTNER NAME',
      dataIndex: 'deliveryPartnerName',
      key: 'deliveryPartnerName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      onCell: (record) => ({
        onClick: () =>
          isDeliveryPartnerPermission &&
          record?.deliveryPartner?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.DELIVERY_PARTNERS}/view/${record?.deliveryPartner?.id}`,
          ),
        className: `${
          isUserPermission &&
          record?.deliveryPartner?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          {record?.deliveryPartner?.firstName ||
          record?.deliveryPartner?.lastName ? (
            <div className="table-data-with-id">{`${
              record?.deliveryPartner?.firstName
                ? record?.deliveryPartner?.firstName
                : ''
            } ${
              record?.deliveryPartner?.lastName
                ? record?.deliveryPartner?.lastName
                : ''
            }`}</div>
          ) : (
            '-'
          )}
          {record?.deliveryPartner?.userNumber ? (
            <div className="font-size-12">
              ({record?.deliveryPartner?.userNumber})
            </div>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: 'LIBRARY NAME',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      align: 'left',
      ellipsis: true,
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () => {
          const library = record?.library;
          return (
            isLibraryPermission &&
            library?.id &&
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${library?.id}/${library?.libraryNumber}`,
            )
          );
        },
        className: `${
          isLibraryPermission && record?.library?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          <div className="table-data-with-id">{record?.library?.name}</div>
          <div className="font-size-12">({record?.library?.libraryNumber})</div>
        </>
      ),
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.pincode?.pincode || '-',
    },
    {
      title: 'PLACED ON',
      dataIndex: 'placedOn',
      key: 'placedOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.WITH_TIME),
      ...filterPopup('createdAt'),
    },
    {
      title: 'ESTIMATED SHIPPING DATE',
      dataIndex: 'deliveredOn',
      key: 'deliveredOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.deliveredOn, DATE_FORMATS?.WITH_TIME) || '-',
    },
    {
      title: 'EXPECTED DELIVERY DATE',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'expectedDeliveryDate' && sortedInfo?.order,
      render: (_, record) =>
        formatDate(record?.expectedDeliveryDate, DATE_FORMATS?.WITH_TIME) ||
        '-',
    },

    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={ORDER_STATUS?.[record?.status]?.color}
          text={ORDER_STATUS?.[record?.status]?.text}
        />
      ),
      ...filterPopup('status', false),
    },
    {
      title: 'ORDER TYPE',
      dataIndex: 'orderType',
      key: 'orderType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.orderType || '-',
    },

    {
      title: 'NOTE',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {record?.cancellationData ||
          record?.comment?.comment ||
          (record?.status === ORDER_STATUS?.NOT_DELIVERED?.value &&
            record?.metadata?.notDeliveredReason) ? (
            <Tooltip title="View comment" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsNotePrompts(true);
                  setOrder(record);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
          {hasUpdatePermission && !isSystemUsersTab && (
            <Button
              type="primary"
              size="small"
              className="table-action-primary-btn"
              onClick={() => {
                setIsCommentPrompts(true);
                setOrder(record);
                setIsOrderAction(true);
              }}
              disabled={
                record?.comment?.isResolved ||
                record?.status !== ORDER_STATUS?.CANCELLED?.value
              }
            >
              Resolve
            </Button>
          )}
        </div>
      ),
    },
    // {
    //   title: 'ACTIONS',
    //   dataIndex: 'actions',
    //   key: 'actions',
    //   render: (_, record) => {
    //     if (
    //       record?.status === ORDER_STATUS?.READY_TO_SHIP?.value ||
    //       record?.status === ORDER_STATUS?.CONFIRMED?.value
    //     ) {
    //       const { firstName, lastName, id, userNumber } =
    //         record?.deliveryPartner || {};
    //       const assignedDeliveryPartner = {
    //         firstName,
    //         lastName,
    //         id,
    //         userNumber,
    //       };
    //       return (
    //         <div className="d-flex">
    //           <UserSelect
    //             size="small"
    //             className="user-select mr-16"
    //             placeholder="Select Delivery Partner"
    //             showAction={['click']}
    //             value={id || null}
    //             userData={id ? assignedDeliveryPartner : null}
    //             roles={USER_ROLES?.DELIVERY?.key}
    //             onChange={(value, userData) => {
    //               setOrder(record);
    //               setDeliveryPartnerData(userData);
    //               setIsDeliveryPartnerPrompts(true);
    //             }}
    //             allowClear={false}
    //           />

    //         </div>
    //       );
    //     }

    //     let selectedValue = null;
    //     if (
    //       record?.status === ORDER_STATUS?.CANCELLED?.value ||
    //       record?.status === ORDER_STATUS?.CONFIRMED?.value
    //     ) {
    //       if (record?.status === ORDER_STATUS?.CANCELLED?.value) {
    //         selectedValue = FULFILLMENT_STATUS?.UNFULFILLED?.value;
    //       } else {
    //         selectedValue = FULFILLMENT_STATUS?.FULFILLED?.value;
    //       }
    //     } else {
    //       selectedValue = null;
    //     }

    //     if (record?.status === ORDER_STATUS?.CANCELLED?.value ||
    //       record?.status === ORDER_STATUS?.CONFIRMED?.value
    //     ) {
    //       return <SelectComponent
    //         size="small"
    //         className="full-width mr-16"
    //         placeholder="Select Action"
    //         showAction={['click']}
    //         value={selectedValue}
    //         showSearch={false}
    //         disabled={
    //           !!selectedValue ||
    //           (record.transferOrderItemCount !== 0 &&
    //             record.transferOrderItemCount !== null)
    //         }
    //         options={NEW_ORDER_TABLE_ACTIONS_OPTIONS}
    //         onSelect={(value) => handleOrderChange(value, record)}
    //         allowClear={false}
    //       />

    //     }
    //     return (
    //       <div className="d-flex">

    //         <div>
    //           {' '}
    //           <div className="action-button">
    //             {hasUpdatePermission && (
    //               <div className="mr-16">
    //                 <Button
    //                   type="primary"
    //                   size="small"
    //                   className="table-action-primary-btn"
    //                   onClick={() => {
    //                     setIsAddCommentModalOpen(true);
    //                     setOrder(record);
    //                     setIsOrderAction(true);
    //                   }}
    //                   disabled={
    //                     record?.status === ORDER_STATUS?.DELIVERED?.value ||
    //                     record?.status === ORDER_STATUS?.CANCELLED?.value
    //                   }
    //                 >
    //                   Cancel
    //                 </Button>
    //               </div>
    //             )}
    //             <div className="mr-16">
    //               <Button
    //                 type="primary"
    //                 size="small"
    //                 className="table-action-primary-btn"
    //                 onClick={() => {
    //                   // eslint-disable-next-line no-undef
    //                   const windowReference = window.open();
    //                   handleChallanDownload(record?.id, windowReference);
    //                 }}
    //                 loading={isChallanLoading?.[record?.id]}
    //                 disabled={
    //                   ![
    //                     ORDER_STATUS?.READY_TO_SHIP?.value,
    //                     ORDER_STATUS?.SHIPPED?.value,
    //                     ORDER_STATUS?.CONFIRMED?.value,
    //                     ORDER_STATUS?.DELIVERED?.value,
    //                     ORDER_STATUS?.NOT_DELIVERED?.value,
    //                   ]?.includes(record?.status)
    //                 }
    //               >
    //                 Download Challan
    //               </Button>
    //             </div>
    //             <Button
    //               type="primary"
    //               size="small"
    //               className="table-action-primary-btn"
    //               onClick={() => handleChallanReset(record?.id)}
    //               loading={isResetLoading?.[record?.id]}
    //               disabled={
    //                 ![
    //                   ORDER_STATUS?.READY_TO_SHIP?.value,
    //                   ORDER_STATUS?.CONFIRMED?.value,
    //                 ]?.includes(record?.status)
    //               }
    //             >
    //               Reset Challan
    //             </Button>
    //             <Tooltip title="Order Status Logs" placement="top" zIndex={4}>
    //               <InfoCircleOutlined
    //                 onClick={() => {
    //                   setOrder(record);
    //                   setIsOrderAction(true);
    //                   setIsOrderStatusModalOpen(true);
    //                 }}
    //                 className="ml-16 pointer svg-icon"
    //               />
    //             </Tooltip>
    //           </div>
    //         </div>
    //       </div>
    //     );
    //   },
    // },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => {
        // Determine fulfillment status
        let selectedValue = null;
        if (
          record?.status === ORDER_STATUS?.CANCELLED?.value ||
          record?.status === ORDER_STATUS?.CONFIRMED?.value
        ) {
          if (record?.status === ORDER_STATUS?.CANCELLED?.value) {
            selectedValue = FULFILLMENT_STATUS?.UNFULFILLED?.value;
          } else {
            selectedValue = FULFILLMENT_STATUS?.FULFILLED?.value;
          }
        }

        // Prepare delivery partner data
        const { firstName, lastName, id, userNumber } =
          record?.deliveryPartner || {};
        const assignedDeliveryPartner = {
          firstName,
          lastName,
          id,
          userNumber,
        };

        return (
          <div className="d-flex" onClick={(e) => e?.stopPropagation()}>
            {/* User Select - Show for READY_TO_SHIP or CONFIRMED */}

            {/* Select Component - Show for CANCELLED or CONFIRMED */}
            {record?.status === ORDER_STATUS?.PLACED?.value && (
              <SelectComponent
                size="small"
                className="full-width mr-16"
                placeholder="Select Action"
                showAction={['click']}
                value={selectedValue}
                showSearch={false}
                disabled={
                  !!selectedValue ||
                  (record.transferOrderItemCount !== 0 &&
                    record.transferOrderItemCount !== null)
                }
                disableFulfill={DISABLE_ORDER}
                record={record}
                options={NEW_ORDER_TABLE_ACTIONS_OPTIONS}
                onSelect={(value) => handleOrderChange(value, record)}
                allowClear={false}
              />
            )}

            {(record?.status === ORDER_STATUS?.READY_TO_SHIP?.value ||
              record?.status === ORDER_STATUS?.CONFIRMED?.value) && (
              <UserSelect
                noPopupContainer
                size="small"
                className="user-select mr-16"
                placeholder="Select Delivery Partner"
                showAction={['click']}
                value={id || null}
                userData={id ? assignedDeliveryPartner : null}
                roles={USER_ROLES?.DELIVERY?.key}
                onChange={(value, userData) => {
                  setOrder(record);
                  setDeliveryPartnerData(userData);
                  setIsDeliveryPartnerPrompts(true);
                }}
                allowClear={false}
              />
            )}

            {/* Action Buttons - Always Show */}
            <div className="action-button">
              {hasUpdatePermission && (
                <div className="mr-16">
                  <Button
                    type="primary"
                    size="small"
                    className="table-action-primary-btn"
                    onClick={() => {
                      setIsAddCommentModalOpen(true);
                      setOrder(record);
                      setIsOrderAction(true);
                    }}
                    disabled={
                      record?.status === ORDER_STATUS?.DELIVERED?.value ||
                      record?.status === ORDER_STATUS?.CANCELLED?.value
                    }
                  >
                    Cancel
                  </Button>
                </div>
              )}

              <div className="mr-16">
                <ChallanDownloadButton
                  order={record}
                  handleChallanDownload={handleChallanDownload}
                  isChallanLoading={isChallanLoading}
                />
              </div>

              <Button
                type="primary"
                size="small"
                className="table-action-primary-btn"
                onClick={() => handleChallanReset(record?.id)}
                loading={isResetLoading?.[record?.id]}
                disabled={
                  ![
                    ORDER_STATUS?.READY_TO_SHIP?.value,
                    ORDER_STATUS?.CONFIRMED?.value,
                  ]?.includes(record?.status)
                }
              >
                Reset Challan
              </Button>

              <Tooltip title="Order Status Logs" placement="top" zIndex={4}>
                <InfoCircleOutlined
                  onClick={() => {
                    setOrder(record);
                    setIsOrderAction(true);
                    setIsOrderStatusModalOpen(true);
                  }}
                  className="ml-16 pointer svg-icon"
                />
              </Tooltip>
            </div>
          </div>
        );
      },
    },
  ]?.filter((item) => {
    if (!isAssignDelPartnerTab && item?.dataIndex === 'deliveryPartnerName') {
      return item?.hidden;
    }

    if (isAssignDelPartnerTab && item?.dataIndex === 'delayReason') {
      return item?.hidden;
    }

    if (isAssignDelPartnerTab && item?.dataIndex === 'totalOrdersCount') {
      return item?.hidden;
    }

    if (isSystemUsersTab && item?.dataIndex === 'actions') {
      return item?.hidden;
    }

    return !item?.hidden;
  });
  const expandedRowRender = (data) => {
    const orderItemList = nestedData?.[data?.id]?.orderItemsAdmin?.data;
    const nestedColumns = [
      {
        title: 'PRODUCT NAME',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        width: 250,
        className: 'max-width-column',
        onCell: (record) => ({
          onClick: () =>
            isToyPermission &&
            record?.toy?.id &&
            openToyDrawer(record?.toy?.id),
          className: `${
            isToyPermission && record?.toy?.id && 'pointer table-cell'
          }`,
        }),
        render: (_, record) => (
          <>
            <div className="table-data-with-id d-flex align-center">
              {record?.toy?.name || '-'}
            </div>
            {record?.toy?.toyNumber && (
              <div className="font-size-12">({record?.toy?.toyNumber})</div>
            )}
          </>
        ),
      },
      {
        title: 'MAPPED UNIT',
        dataIndex: 'inventoryUnits',
        key: 'inventoryUnits',
        width: 200,
        align: 'left',
        className: 'max-width-column',
        onCell: () => ({
          onClick: (e) => e?.stopPropagation(),
        }),
        render: (_, record) => (
          <MappedUnitsCell units={record?.inventoryUnits} />
        ),
      },
      {
        title: 'IMAGES',
        dataIndex: 'images',
        key: 'images',
        ellipsis: true,
        width: 100,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => {
          const images = record?.toy?.images;
          return (
            <span>
              {images?.length > 0 ? (
                <>
                  <Image
                    preview={{
                      visible: false,
                    }}
                    width={20}
                    height={20}
                    src={images?.[0]?.url}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setVisible(true);
                      setToyImages(images);
                    }}
                    className="pointer"
                  />
                </>
              ) : (
                <span>No Images</span>
              )}
              {images?.length > 1 ? ` + ${images?.length - 1}` : ''}
            </span>
          );
        },
      },
      {
        title: 'BRAND',
        dataIndex: 'brand',
        key: 'brand',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => record?.toy?.brand?.name || '-',
      },
      {
        title: 'STATUS',
        dataIndex: 'status',
        key: 'status',
        ellipsis: true,
        width: 180,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => {
          const config =
            FULFILLMENT_STATUS?.[record?.status] ||
            ORDER_STATUS?.[record?.status];
          if (!record?.status) return '-';
          return (
            <Badge
              color={config?.color}
              text={config?.text || humanizeEnum(record?.status)}
            />
          );
        },
      },
      {
        title: 'STOCK AT BOOKING',
        dataIndex: 'stockAtBooking',
        key: 'stockAtBooking',
        align: 'left',
        width: 150,
        render: (_, record) => record?.stockAtBooking ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="AVAILABLE FOR BOOKING"
            info={STOCK_COLUMN_TOOLTIPS.AVAILABLE_FOR_BOOKING}
          />
        ),
        dataIndex: 'availableStock',
        key: 'availableStock',
        align: 'left',
        width: 220,
        render: (_, record) => record?.libraryInventory?.availableStock ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="PHYSICALLY AVAILABLE STOCK"
            info={STOCK_COLUMN_TOOLTIPS.PHYSICALLY_AVAILABLE_STOCK}
          />
        ),
        dataIndex: 'physicallyAvailableStock',
        key: 'physicallyAvailableStock',
        align: 'left',
        ellipsis: true,
        className: 'max-width-column',
        width: 260,
        render: (_, record) =>
          record?.libraryInventory?.physicallyAvailableStock ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="BLOCKED UNITS"
            info={STOCK_COLUMN_TOOLTIPS.BLOCKED_UNITS}
          />
        ),
        dataIndex: 'pendingDispatchCount',
        key: 'pendingDispatchCount',
        align: 'left',
        ellipsis: true,
        className: 'max-width-column',
        width: 260,
        render: (_, record) =>
          record?.libraryInventory?.pendingDispatchCount ?? '-',
      },
      {
        title: 'PACKAGING MATERIAL',
        dataIndex: 'packagingMaterial',
        key: 'packagingMaterial',
        ellipsis: true,
        width: 250,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => (
          <>
            {record?.toy?.bag ? (
              <div>
                <span className="mr-8">
                  {record?.toy?.bag?.name} ({record?.toy?.bag?.size})
                </span>
                <Image
                  preview={{ visible: false }}
                  width={22}
                  src={record?.toy?.bag?.image}
                  onClick={(e) => {
                    e?.stopPropagation();
                    setPreviewVisible(true);
                    setToyPreviewImage(record?.toy?.bag?.image);
                  }}
                  className="pointer"
                />
              </div>
            ) : (
              '-'
            )}
          </>
        ),
      },
      {
        title: 'AGE GROUPS',
        dataIndex: 'ageGroups',
        key: 'ageGroups',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.toy?.ageGroups?.map((ageGroup) =>
            ageGroup?.minAge > 18 ? (
              <Tag key={ageGroup?.id}>
                {Math.floor(ageGroup?.minAge / 12)} -{' '}
                {Math.floor(ageGroup?.maxAge / 12)} Years
              </Tag>
            ) : (
              <Tag key={ageGroup?.id}>
                {ageGroup?.minAge} - {ageGroup?.maxAge} Months
              </Tag>
            ),
          ),
      },
      {
        title: 'FULFILLMENT STATUS',
        dataIndex: 'fulfillmentStatus',
        key: 'fulfillmentStatus',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ? (
            <Badge
              color={FULFILLMENT_STATUS?.[record?.status]?.color}
              text={FULFILLMENT_STATUS?.[record?.status]?.text}
            />
          ) : (
            '-'
          ),
      },
      {
        title: 'EXPECTED RETURN',
        dataIndex: 'returnDate',
        key: 'returnDate',
        ellipsis: true,
        align: 'left',
        width: 200,
        className: 'max-width-column',
        render: (_, record) =>
          formatDate(record?.returnDate, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'NOTE',
        dataIndex: 'note',
        key: 'note',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => (
          <div className="action-button" onClick={(e) => e?.stopPropagation()}>
            {record?.cancellationData?.type ===
            CANCELLATION_TYPE?.LIBRARY?.value ? (
              <Tooltip title="View comment" placement="top" zIndex={4}>
                <CommentIcon
                  className="mr-24 pointer svg-icon"
                  onClick={() => {
                    setIsNotePrompts(true);
                    setOrderItem(record);
                    setIsOrderAction(false);
                  }}
                />
              </Tooltip>
            ) : (
              <span className="mr-24">-</span>
            )}
          </div>
        ),
      },
      {
        title: 'FULFILMENT POSSIBILITY',
        dataIndex: 'fulfilmentPossibility',
        key: 'fulfilmentPossibility',
        align: 'left',
        width: 200,
        hidden: isAssignDelPartnerTab || isSystemUsersTab,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPossibilityItem(record);
              setPossibilityDrawerVisible(true);
            }}
          >
            View
          </Button>
        ),
      },
      ...getStaffColumns(
        () => data?.library?.id,
        !isUnitsFeatureEnabledForLibrary(data?.library?.id),
      ),
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        render: (_, record) => (
          <div className="d-flex" onClick={(e) => e?.stopPropagation()}>
            {data?.status === ORDER_STATUS.PLACED?.value && (
              <SelectComponent
                size="small"
                className="full-width mr-16"
                placeholder="Select Action"
                showSearch={false}
                value={
                  record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
                  record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value
                    ? record?.status
                    : null
                }
                disabled={
                  record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
                  record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                  record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
                  (record?.transferOrders?.length > 0 &&
                    record?.transferOrders?.[0]?.status !==
                      TRANSFER_ORDER_STATUS.RECEIVED.value)
                }
                disableFulfill={DISABLE_ORDER_ITEM}
                record={record}
                options={NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS}
                onChange={(value) => handleOrderItemChange(value, record, data)}
                allowClear={false}
              />
            )}
            <div className="action-button">
              <div className="mr-16">
                <Button
                  type="primary"
                  size="small"
                  className="table-action-primary-btn"
                  onClick={() => {
                    // eslint-disable-next-line no-undef
                    const windowReference = window.open();
                    handleStickerDownload(record?.id, windowReference);
                  }}
                  loading={isStickerLoading?.[record?.id]}
                  disabled={
                    record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                    ![
                      ORDER_STATUS?.CONFIRMED?.value,
                      ORDER_STATUS?.READY_TO_SHIP?.value,
                    ]?.includes(data?.status)
                  }
                >
                  Download Sticker
                </Button>
              </div>
              <div className="mr-16">
                <Button
                  type="primary"
                  size="small"
                  className="table-action-primary-btn"
                  onClick={() => handleStickerReset(record?.id)}
                  loading={isResetStickerLoading?.[record?.id]}
                  disabled={
                    record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                    ![
                      ORDER_STATUS?.CONFIRMED?.value,
                      ORDER_STATUS?.READY_TO_SHIP?.value,
                    ]?.includes(data?.status)
                  }
                >
                  Reset Sticker
                </Button>
              </div>
              <Button
                type="primary"
                size="small"
                className="table-action-primary-btn"
                onClick={() => {
                  setOrderItem(record);
                  setIsOrderAction(false);
                  setIsContentMasterModalOpen(true);
                }}
              >
                View Content Logs
              </Button>
              <Tooltip
                title="Order Item Status Logs"
                placement="top"
                zIndex={4}
              >
                <InfoIcon
                  onClick={() => {
                    setOrderItem(record);
                    setIsOrderAction(false);
                    setIsOrderStatusModalOpen(true);
                  }}
                  className="ml-16 pointer svg-icon"
                />
              </Tooltip>
            </div>
          </div>
        ),
      },
    ]?.filter((item) => {
      if (
        (isAssignDelPartnerTab || isSystemUsersTab) &&
        item?.dataIndex === 'actions'
      ) {
        return item?.hidden;
      }
      return !item?.hidden;
    });

    return (
      <CommonTable
        loading={isLoading[data?.id]}
        columns={nestedColumns}
        dataSource={orderItemList || []}
        pagination={false}
        rowKey={(record) => record?.id}
        className="nested-table"
      />
    );
  };

  const expandIcon = ({ expanded, onExpand, record }) =>
    expanded ? (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded disabled"
        aria-label="Expand row"
        aria-expanded="false"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    ) : (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
        aria-label="Collapse row"
        aria-expanded="true"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    );

  const handleCancel = () => {
    setIsContentMasterModalOpen(false);
    setOrderItem({});
  };

  const locale = {
    emptyText: isEmptyOrderList ? '' : <span />,
  };

  const selectedOrdersUniqueTags = useMemo(() => {
    const selectedOrders = (selectedRowKeys || [])
      .map((id) => (orderList || []).find((o) => o?.id === id))
      .filter(Boolean);

    return Array.from(
      new Map(
        selectedOrders
          ?.flatMap((o) => o?.orderTags || [])
          ?.filter((t) => t?.id)
          ?.map((t) => [t?.id, t]),
      ).values(),
    );
  }, [orderList, selectedRowKeys]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleOrderScroll = () => {
    if (!isFetchMoreOrderLoading && !isOrderLoading && hasMore) {
      const skip = orderList?.length;
      setPaginationProp({ ...paginationProp, skip });
      setIsOrderLoading(true);
      setNestedData({});
      setExpandedRowKeys([]);
      userOrdersWithItems({
        variables: {
          where: {
            id: userId,
          },
          filter: {
            ...initialNewDeliveryFilter,
            skip,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
  };

  const infiniteScrollRef = useInfiniteScroll(handleOrderScroll, isActive);

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsOrderLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setOrderList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);
    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });
    }

    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
    preserveSelectedRowKeys: true,
  };

  const handleSelectOrders = (key) => {
    setSelectedRowKeys((prev) => {
      if (prev?.includes(key)) {
        return prev?.filter((item) => item !== key);
      }
      return [...prev, key];
    });
  };

  const findSelectableOrders = () => orderList || [];

  const handleSelectAllMobile = (checked) => {
    if (checked) {
      setSelectedRowKeys((prev) => {
        const newIds = (orderList || [])?.map((data) => data?.id);
        return Array.from(new Set([...prev, ...newIds]));
      });
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleFilter = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsOrderLoading(true);
    setIsAllFilterModalOpen(false);
    setOrderList([]);
    setIsActive(true);
    const filters = {
      status: values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      deliveredOn: values?.deliveredOn?.[1]
        ? { from: values?.deliveredOn?.[0], to: values?.deliveredOn?.[1] }
        : null,
      pincodes: values?.pincodes,
      deliveryType: values?.deliveryType,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    userOrdersWithItems({
      variables: {
        where: {
          id: userId,
        },
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          status: values?.status?.length > 0 ? values?.status : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  return (
    <>
      <ContentMasterModal
        isModalOpen={isContentMasterModalOpen}
        contentHistory={orderItem?.contentHistory}
        handleCancel={handleCancel}
      />
      <ItemStatusModal
        title={isOrderAction ? 'Order Status Logs' : 'Order Item Status Logs'}
        isModalOpen={isOrderStatusModalOpen}
        setIsModalOpen={setIsOrderStatusModalOpen}
        statusHistories={
          isOrderAction ? order?.statusHistory : orderItem?.statusHistory
        }
        isOrderAction={isOrderAction}
      />
      <ResolveCommentModal
        form={form}
        isResolved={false}
        isModalOpen={isCommentPrompts}
        setIsModalOpen={setIsCommentPrompts}
        isBtnLoading={isCommentLoading}
        onFinish={handleAddComment}
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isDeliveryPartnerPrompts}
        onOk={() =>
          handleAssignDeliveryPartner([order?.id], deliveryPartnerData?.value)
        }
        onCancel={() => setIsDeliveryPartnerPrompts(false)}
        okButtonProps={{ loading: isDeliveryPartnerLoading }}
      >
        Are you sure you want to assign
        <strong> {deliveryPartnerData?.children} </strong>?
      </Modal>
      <DelayReasonModal
        note={
          !isEmpty(order?.metadata?.delayReason)
            ? order?.metadata
            : tempComment?.current?.[order?.id]
        }
        isModalOpen={isReasonPrompt}
        setIsModalOpen={setIsReasonPrompt}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isFulfillPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsFulfillPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            setIsFulfillPrompts(false);
            handleFulfillOrderItem();
          }
        }}
        onCancel={() => setIsFulfillPrompts(false)}
        okButtonProps={{ loading: isFulfillLoading }}
      >
        Are you sure you want to
        <strong> Mark as fulfill </strong>
        this order item?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isReadyPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsReadyPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            handleReadyOrder([order?.id]);
            setIsReadyPrompts(false);
          }
        }}
        onCancel={() => setIsReadyPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Mark as ready </strong>
        this order?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isConfirmPrompts}
        onOk={() => handleReadyOrder(selectedRowKeys)}
        onCancel={() => setIsConfirmPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Confirm </strong>
        this orders?
      </Modal>
      {isDelayReasonPrompts && (
        <AddDelayReasonModal
          form={delayForm}
          isOrderAction={isOrderAction}
          isModalOpen={isDelayReasonPrompts}
          setIsModalOpen={setIsDelayReasonPrompts}
          isBtnLoading={isReadyLoading}
          onFinish={handleAddDelayReason}
        />
      )}
      <AddCommentModal
        form={form}
        isOrderAction={isOrderAction}
        isModalOpen={isAddCommentModalOpen}
        setIsModalOpen={setIsAddCommentModalOpen}
        isBtnLoading={isAddCommentLoading}
        onFinish={
          isOrderAction ? handleAddCommentInOrder : handleAddCommentInOrderItem
        }
      />
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <CommonPreview
        visible={previewVisible}
        setVisible={setPreviewVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <NoteModal
        note={
          isOrderAction ? order?.cancellationData : orderItem?.cancellationData
        }
        isModalOpen={isNotePrompts}
        setIsModalOpen={setIsNotePrompts}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
        order={order}
      />
      <AssignDeliveryPartnerModal
        form={assignDelPartnerForm}
        onFinish={(values) =>
          handleAssignDeliveryPartner(selectedRowKeys, values?.name)
        }
        loadings={isDeliveryPartnerLoading}
        isModalOpen={isDeliveryPartnerModalOpen}
        setIsModalOpen={setDeliveryPartnerModalOpen}
        title="Assign Delivery Partner"
      />
      <FulfilmentPossibilityDrawer
        visible={possibilityDrawerVisible}
        onClose={() => setPossibilityDrawerVisible(false)}
        libraryInventoryId={selectedPossibilityItem?.libraryInventory?.id}
        physicallyAvailableStock={
          selectedPossibilityItem?.libraryInventory?.physicallyAvailableStock
        }
        isDesktop={isDesktop}
      />
      {/* <div className="d-flex justify-end align-center mb-16">
        {isAssignDelPartnerTab ? (
          <Button
            type="primary"
            disabled={!hasSelected}
            className="mr-8"
            onClick={() => setDeliveryPartnerModalOpen(true)}
          >
            Assign Delivery Partner
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={!hasSelected}
            className="mr-8"
            onClick={() => setIsConfirmPrompts(true)}
          >
            Confirm Orders
          </Button>
        )}
        <SearchComponent
          className="search-box"
          query={orderSearchTerm}
          setQuery={setOrderSearchTerm}
          getData={handleSearch}
        />
      </div> */}

      {isDesktop && (
        <div className="d-flex justify-end mb-16 flex-wrap border border-gray-200">
          <BulkActionButton
            selectedItemIds={selectedRowKeys}
            actions={ORDER_TAG_BULK_ACTIONS}
            scope="SYSTEM_USERS_END_USERS"
            supportsNestedSubmit
            selectedTagsForRemove={selectedOrdersUniqueTags}
            drawerTitle="Order Bulk Actions"
            className="mr-8"
            onSuccess={() => handleSearch(orderSearchTerm)}
            onClearSelection={() => setSelectedRowKeys([])}
            tagConfigSubtype="ORDERS"
            useOrderTagsApi
            orderTagTargetField="orders"
          />
        </div>
      )}

      {!isDesktop && (
        <div className="d-flex justify-end align-center">
          <BulkActionButton
            selectedItemIds={selectedRowKeys}
            actions={ORDER_TAG_BULK_ACTIONS}
            scope="SYSTEM_USERS_END_USERS"
            supportsNestedSubmit
            selectedTagsForRemove={selectedOrdersUniqueTags}
            drawerTitle="Order Bulk Actions"
            className="mr-8"
            onSuccess={() => handleSearch(orderSearchTerm)}
            onClearSelection={() => setSelectedRowKeys([])}
            tagConfigSubtype="ORDERS"
            useOrderTagsApi
            orderTagTargetField="orders"
          />
          <Checkbox
            indeterminate={
              selectedRowKeys?.length < findSelectableOrders()?.length &&
              selectedRowKeys?.length > 0
            }
            checked={
              selectedRowKeys?.length > 0 &&
              selectedRowKeys?.length === findSelectableOrders()?.length &&
              !isOrderLoading &&
              !isFetchMoreOrderLoading
            }
            onChange={(e) => {
              handleSelectAllMobile(e?.target?.checked);
            }}
            className="ml-16"
          >
            Select all
          </Checkbox>
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
          <div className="ml-16 mr-16 d-flex align-center">
            <SortDropdown
              isOpen={isAllSortPopoverOpen}
              setIsOpen={setIsAllSortPopoverOpen}
              options={map(sort, (item) => item)}
              sortedInfo={sortedInfo}
              setSortedInfo={setSortedInfo}
              onFinish={handleSort}
            />
          </div>
        </div>
      )}
      {isDesktop ? (
        <CommonTable
          locale={locale}
          columns={columns}
          data={orderList || []}
          loading={isOrderLoading}
          rowSelection={rowSelection}
          className={`nested-table combined-nested-table ${
            paginationProp?.total <= LIMIT ? 'mb-32' : ''
          }`}
          onChange={handleTableChange}
          rowKey={(record) => record?.id}
          expandedRowKeys={expandedRowKeys}
          onExpand={(_, record) => setOrder(record)}
          onRow={() => ({
            style: { cursor: 'pointer' },
          })}
          paginationConfig={
            paginationProp?.total > LIMIT ? paginationProp : false
          }
          expandable={{
            onExpand: (expanded, record) => handleExpand(expanded, record),
            expandedRowRender: (record) => expandedRowRender(record),
            expandIcon,
            expandRowByClick: true,
          }}
        />
      ) : (
        <div className="mobile-container-combined no-scroll-bar">
          <div
            className="d-flex justify-start align-center text-primary pr-12 pl-12"
            onClick={() => {
              history.push(`${ROUTES?.ORDERS}${ROUTES?.COMBINED_ORDERS}`);
            }}
          >
            <ArrowLeftOutlined className="mr-8" />
            Go Back
          </div>
          <AllFilterOrderModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            isNewPath={false}
            isCombinedOrder
            isAdmin
          />
          <AllOrderItemModal
            order={order}
            isNewPath={
              order?.status === ORDER_STATUS?.PLACED?.value ||
              order?.status === ORDER_STATUS?.CONFIRMED?.value
            }
            isCombinedOrder
            setOrder={setOrder}
            isModalOpen={isAllOrderItemModalOpen}
            setIsModalOpen={setIsAllOrderItemModalOpen}
            handleExpand={handleExpand}
            nestedData={nestedData}
            isLoading={isLoading}
            setIsNotePrompts={setIsNotePrompts}
            setOrderItem={setOrderItem}
            setIsOrderAction={setIsOrderAction}
            handleOrderItemChange={handleOrderItemChange}
            handleStickerDownload={handleStickerDownload}
            isStickerLoading={isStickerLoading}
            handleStickerReset={handleStickerReset}
            isResetStickerLoading={isResetStickerLoading}
            setIsContentMasterModalOpen={setIsContentMasterModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            isAdmin
            setIsOrderStatusModalOpen={setIsOrderStatusModalOpen}
          />
          <AllOrderList
            orderList={orderList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyOrderList={isEmptyOrderList}
            isOrderLoading={isOrderLoading}
            setOrder={setOrder}
            isNewPath={
              order?.status === ORDER_STATUS?.PLACED?.value ||
              order?.status === ORDER_STATUS?.CONFIRMED?.value
            }
            isCombinedOrder
            setIsNotePrompts={setIsNotePrompts}
            setIsOrderAction={setIsOrderAction}
            handleOrderChange={handleOrderChange}
            setIsAllOrderItemModalOpen={setIsAllOrderItemModalOpen}
            handleChallanDownload={handleChallanDownload}
            isChallanLoading={isChallanLoading}
            handleChallanReset={handleChallanReset}
            isResetLoading={isResetLoading}
            setVisible={setVisible}
            setToyImages={setToyImages}
            setIsReasonPrompt={setIsReasonPrompt}
            isAdmin
            setIsOrderStatusModalOpen={setIsOrderStatusModalOpen}
            setDeliveryPartnerData={setDeliveryPartnerData}
            setIsDeliveryPartnerPrompts={setIsDeliveryPartnerPrompts}
            handleSelectOrders={handleSelectOrders}
            selectedOrders={selectedRowKeys}
          />
        </div>
      )}
    </>
  );
};

export default NewOrderTable;
